import { Link } from "react-router-dom";
import { StarDisplay } from "./StarRating";
import { useState } from "react";
import StatusBadge from "./StatusBadge";
import { formatCLP, formatUSD, resolveImageUrl, computeSale, AVX_FORMATS } from "../lib/format";
import { useCart } from "../contexts/CartContext";
import { useCampaign } from "../contexts/CampaignContext";

export default function PluginCard({ product, ctaLabel = "View details" }) {
    const sale = computeSale(product);
    const { cart, addItem } = useCart();
    const { campaign } = useCampaign();
    const cyberActive = !!(campaign && campaign.active && sale);
    const inCart = (cart.items || []).some((it) => it.key === `p:${product.id}`);
    const [adding, setAdding] = useState(false);

    // Only show the "+ cart" quick button for buyable items (published + priced)
    const priced = ((product.price_clp || 0) > 0) || ((product.price_usd || 0) > 0);
    const buyable = product.status === "published" && priced;

    const handleAdd = async (e) => {
        e.preventDefault();
        e.stopPropagation();
        if (adding || inCart) return;
        setAdding(true);
        await addItem({ product_id: product.id });
        setAdding(false);
    };

    return (
        <Link
            to={`/plugins/${product.slug}`}
            data-testid={`plugin-card-${product.slug}`}
            className="group panel-elevated overflow-hidden block transition-all duration-200 hover:border-avx-blue/60 hover:-translate-y-0.5 hover:shadow-[0_0_28px_rgba(245,197,66,0.15)]"
        >
            <div className="relative aspect-[16/10] overflow-hidden bg-avx-surface">
                {product.image_url ? (
                    <img
                        src={resolveImageUrl(product.image_url)}
                        alt={product.name}
                        loading="lazy"
                        decoding="async"
                        className="w-full h-full object-cover opacity-80 group-hover:opacity-100 transition-opacity duration-300"
                    />
                ) : (
                    <div className="w-full h-full bg-gradient-to-br from-avx-elevated to-avx-bg" />
                )}
                <div className="absolute inset-0 bg-gradient-to-t from-avx-bg via-avx-bg/30 to-transparent" />
                <div className="absolute top-3 left-3">
                    <StatusBadge status={product.status} testId={`plugin-card-status-${product.slug}`} />
                </div>
                {sale && (
                    <div
                        className="absolute top-3 right-3 flex flex-col items-end gap-1.5"
                        data-testid={`plugin-card-sale-${product.slug}`}
                    >
                        {cyberActive && (
                            <span
                                className="font-heading font-black text-[10px] tracking-[0.15em] px-2 py-1 rounded-sm text-white shadow-lg"
                                style={{ background: "linear-gradient(90deg,#FF2E9A,#7C3AED)" }}
                                data-testid={`plugin-card-cyberday-${product.slug}`}
                            >
                                {campaign.badge_text || "CYBERDAY"}
                            </span>
                        )}
                        <span className="bg-avx-danger text-white font-heading font-black text-sm px-2.5 py-1 rounded-sm shadow-lg">
                            −{sale.discount_pct}%
                        </span>
                    </div>
                )}
                <div className="absolute bottom-3 left-3 right-3 flex justify-between items-end">
                    <div className="flex gap-1">
                        {AVX_FORMATS.map((f) => (
                            <span key={f} className="px-1.5 py-0.5 rounded-sm border border-avx-blue/40 bg-avx-bg/80 text-avx-blue text-[9px] font-mono uppercase tracking-[0.2em]">
                                {f}
                            </span>
                        ))}
                    </div>
                </div>
            </div>
            <div className="p-5">
                <h3 className="font-heading font-bold text-lg leading-tight mb-1 group-hover:text-avx-blue transition-colors">
                    {product.name}
                </h3>
                {(product.categories || []).length > 0 && (
                    <div className="flex flex-wrap gap-1 mb-2" data-testid={`plugin-card-categories-${product.slug}`}>
                        {(product.categories || []).slice(0, 3).map((c) => (
                            <span key={c.id} className="px-1.5 py-0.5 rounded-sm bg-avx-blue/10 border border-avx-blue/30 text-avx-blue text-[9px] font-mono uppercase tracking-[0.15em]">
                                {c.name}
                            </span>
                        ))}
                    </div>
                )}
                {product.rating && product.rating.count > 0 && (
                    <StarDisplay value={product.rating.average} count={product.rating.count} size={13} className="mb-2" />
                )}
                <p className="text-sm text-avx-muted line-clamp-2 leading-relaxed mb-4 min-h-[2.6rem]">
                    {product.short_description}
                </p>
                <div className="flex items-end justify-between">
                    <div>
                        {product.status === "coming_soon" ? (
                            <span className="label-mono text-avx-info">Coming soon</span>
                        ) : !((product.price_clp || 0) > 0) && !((product.price_usd || 0) > 0) ? (
                            <span className="font-heading font-black text-avx-success text-base tracking-tight" data-testid="plugin-card-free-badge">
                                FREE
                            </span>
                        ) : sale ? (
                            <>
                                <div className="flex items-baseline gap-2">
                                    <div className="font-mono text-avx-muted text-xs line-through">{formatCLP(sale.base_clp)}</div>
                                    <div className="font-mono text-avx-danger text-base font-semibold">{formatCLP(sale.sale_clp)}</div>
                                </div>
                                <div className="font-mono text-xs text-avx-muted">{formatUSD(sale.sale_usd)}</div>
                            </>
                        ) : (
                            <>
                                <div className="font-mono text-avx-blue text-base font-semibold">
                                    {formatCLP(product.price_clp)}
                                </div>
                                <div className="font-mono text-xs text-avx-muted">{formatUSD(product.price_usd)}</div>
                            </>
                        )}
                    </div>
                    <span className="text-[10px] font-mono uppercase tracking-[0.2em] text-avx-muted group-hover:text-avx-blue transition-colors">
                        {ctaLabel} →
                    </span>
                </div>
                {cyberActive && (sale.base_clp - sale.sale_clp) > 0 && (
                    <div
                        className="mt-3 inline-flex items-center gap-1.5 text-[11px] font-heading font-black uppercase tracking-[0.12em] px-2.5 py-1 rounded-full text-white shadow"
                        style={{ background: "linear-gradient(90deg,#FF2E9A,#7C3AED)" }}
                        data-testid={`plugin-card-savings-${product.slug}`}
                    >
                        You save {formatCLP(sale.base_clp - sale.sale_clp)}
                    </div>
                )}
                {buyable && (
                    <button
                        type="button"
                        onClick={handleAdd}
                        disabled={adding || inCart}
                        data-testid={`plugin-card-add-cart-${product.slug}`}
                        className={`mt-3 w-full text-[10px] font-mono uppercase tracking-[0.18em] py-2 rounded-sm border transition-colors ${
                            inCart
                                ? "border-avx-success/50 bg-avx-success/8 text-avx-success cursor-default"
                                : "border-avx-line bg-avx-bg/60 text-avx-text/85 hover:border-avx-blue/60 hover:text-avx-blue disabled:opacity-60"
                        }`}
                    >
                        {inCart ? "✓ In cart" : (adding ? "Adding…" : "+ Add to cart")}
                    </button>
                )}
            </div>
        </Link>
    );
}
